import type { ReactNode } from 'react';
import { useLegacyIcon } from '../../view/icons.ts';
import { CARD_BUTTON, CARD_COLORS, CardButton, PickerCard, stopEvent } from '../../view/picker_card.tsx';
import { isSameValue, type ModeChoice } from './status_tile.ts';

// A status tile's mode card (docs/specs/status-tile.md ST-5): the states to choose from, each with
// its icon and text, the current one filled; a tap on one sends it at once and closes the card
// (QS-2). Cancel and Escape close it without sending.

export interface ModePickerProps {
    /** The tile's place on the page. */
    anchor: DOMRect;
    title: string;
    choices: readonly ModeChoice[];
    /** What the source reads now. */
    current: unknown;
    onChoose(choice: ModeChoice) : void;
    onClose() : void;
}

/** A state's icon in the DOM: the icon's path in an SVG, in its colour. */
function ChoiceIcon({ name, color }: { name: string | undefined; color: string }) : ReactNode {
    const icon = useLegacyIcon(name);
    if (!icon) {
        return <span style={{ width: 26, flex: 'none' }} />;
    }

    const [width, height, , , path] = icon.icon;
    return (
        <svg viewBox={`0 0 ${width} ${height}`} width={26} height={26} aria-hidden="true" style={{ flex: 'none' }}>
            {(Array.isArray(path) ? path : [path]).map((each) => (
                <path key={each} d={each} fill={color} />
            ))}
        </svg>
    );
}

export function ModePicker({ anchor, title, choices, current, onChoose, onClose }: ModePickerProps) : ReactNode {
    return (
        <PickerCard anchor={anchor} title={title} label="mode" onClose={onClose}>
            <div role="listbox" aria-label={title} style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: '16px 0' }}>
                {choices.map((choice) => {
                    const isCurrent = isSameValue(choice.value, current);
                    return (
                        <button
                            key={String(choice.value)}
                            type="button"
                            role="option"
                            aria-selected={isCurrent}
                            onClick={(event) => {
                                stopEvent(event);
                                onChoose(choice);
                            }}
                            style={{
                                ...CARD_BUTTON,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                minHeight: 48,
                                padding: '0 14px',
                                borderRadius: 10,
                                background: isCurrent ? CARD_COLORS.accent : CARD_COLORS.raised,
                                fontSize: 17,
                                textAlign: 'left',
                            }}
                        >
                            {/* On the accent, the icon in white; elsewhere in its state's colour. */}
                            <ChoiceIcon name={choice.icon} color={isCurrent ? '#ffffff' : (choice.color ?? '#ffffff')} />
                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{choice.text}</span>
                        </button>
                    );
                })}
            </div>
            <div style={{ display: 'flex' }}>
                <CardButton label="Cancel" onPress={onClose} />
            </div>
        </PickerCard>
    );
}
